<template>
  <div
    ref="imageViewer"
    class="image-viewer"
  >
    <img
      ref="imageElement"
      :src="imageUrl"
      alt="document"
      class="image-canvas"
      @load="onImageLoad"
    />
    <canvas
      ref="highlightCanvas"
      class="highlight-canvas"
      @mousemove="onCanvasMove"
      @mouseleave="onCanvasLeave"
      @click="onCanvasClick"
    />
    <div
      ref="cursorBadge"
      class="cursor-badge"
      :style="{ transform: `translate(calc(${x}px - 100%), ${y}px)` }"
    >
      <div
        class="cursor-badge-label"
        :class="{ 'is-active': (zones?.length || 0) > 0 }"
      >
        <div
          v-for="zone in zones"
          :key="zone.id"
          class="cursor-badge-zones"
        >
          <span
            v-for="part in zone.parts"
            :key="part"
            class="cursor-badge-part"
          >
            {{ part }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onBeforeUnmount, onMounted, watch } from 'vue';
import { useInspectorStore } from '@stores/documentInspectorStore.js';
import { prettifyMixedCase } from '@frontend/shared/Util.js';

const inspectorStore = useInspectorStore();

let raf = null;

const props = defineProps({
  imageUrl: {
    type: String,
    default: '',
    required: true,
  },
});

const imageViewer = ref(null);
const imageElement = ref(null);
const highlightCanvas = ref(null);
const cursorBadge = ref(null);
const x = ref(0);
const y = ref(0);

const onImageLoad = () => {
  inspectorStore.setImageDimensions(
      imageElement.value.naturalWidth,
      imageElement.value.naturalHeight,
  );
};

const onCanvasMove = e => {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = null;

    const canvas = highlightCanvas.value;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // normalized to [0..1] - same as PDF logic
    const nx = x / rect.width;
    const ny = y / rect.height;

    inspectorStore.setCurrentCursorPosition(nx, ny);
  });
};

const onCanvasLeave = () => {
  const canvas = highlightCanvas.value;
  if (canvas) canvas.style.cursor = 'default';
  inspectorStore.setCurrentCursorPosition(null, null);
};

const onCanvasClick = e => {
  const canvas = highlightCanvas.value;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const nx = x / rect.width;
  const ny = y / rect.height;

  inspectorStore.handleCanvasClick(nx, ny);
};

const handleMove = e => {
  x.value = e.clientX;
  y.value = e.clientY;
};

const zones = computed(() => {
  if (inspectorStore.hoveredZones.size === 0) {
    return [];
  }

  return [...inspectorStore.hoveredZones].map(zone => {
    const parts = zone.split('.');
    const prettyParts = parts.map(p => prettifyMixedCase(p));
    return {
      id: zone,
      parts: prettyParts,
    };
  });
});

onMounted(async () => {
  inspectorStore.setDOMElements(imageViewer.value, null, highlightCanvas.value, cursorBadge.value);
  window.addEventListener('pointermove', handleMove, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', handleMove);
});

watch(() => props.imageUrl, () => {
  if (imageElement.value) {
    onImageLoad();
  }
});
</script>

<style scoped>
.image-viewer {
  background-color: var(--stk-border);
  display: flex;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.image-canvas {
  max-width: 100%;
  width: 100%;
  display: block;
}

.highlight-canvas {
  position: absolute;
  left: 0;
  top: 0;
  max-width: 100%;
  width: 100%;
}

/* Reuse cursor-badge styles from InspectorPDF.vue */
.cursor-badge {
  display: flex;
  left: 0;
  pointer-events: none;
  position: fixed;
  top: 0;
  user-select: none;
  z-index: 10;
}

.cursor-badge-label {
  background: #fff;
  border: 1px solid var(--stk-control-border-color);
  border-radius: 0.5em;
  box-shadow: var(--stk-control-shadow);
  color: var(--stk-text-strong);
  opacity: 0;
  font-size: 0.75em;
  font-weight: 600;
  padding: 0.25em 0.5em;
  transform: scale(0.7);
  transform-origin: top right;
  transition-property: opacity, transform;
  transition-duration: 200ms;
  transition-timing-function: ease-out;
  user-select: none;
  will-change: opacity, transform;
  white-space: nowrap;

  &.is-active {
    opacity: 1;
    transform: none;
  }
}

.cursor-badge-zones {
  display: flex;
  gap: 0.25em;
}

.cursor-badge-part {
  display: flex;
  gap: 0.25em;

  &:not(:last-child) {
    color: var(--stk-text-normal);
    opacity: 0.5;

    &::after {
      color: var(--stk-text-weak);
      content: "/";
    }
  }
}
</style>
